import React from "react";
import { useStoreSettings } from "../../../../../hooks/useStoreSettings";

const VariantSelector = ({
    variants = [],
    options = [],
    selectedVariants = [],
    onVariantSelect
}) => {
    // Get store settings for currency
    const { currency: storeCurrency } = useStoreSettings();

    // Get display name for a variant based on its properties
    const getVariantDisplayName = (variant) => {
        const parts = [];

        // Dynamically build display name from variant properties
        // Skip standard fields like id, price, quantity, sku
        const skipFields = ['id', 'price', 'quantity', 'sku', 'images'];

        Object.keys(variant).forEach(key => {
            if (!skipFields.includes(key) && variant[key]) {
                // Handle multilingual values consistently
                let displayValue = variant[key];
                try {
                    const valueData = JSON.parse(displayValue);
                    if (typeof valueData === 'object' && valueData !== null) {
                        displayValue = valueData.en || Object.values(valueData)[0] || displayValue;
                    }
                } catch {
                    // Use value as-is if not JSON
                }

                // Capitalize the key and format it nicely
                const label = key.charAt(0).toUpperCase() + key.slice(1);
                parts.push(`${label}: ${displayValue}`);
            }
        });

        return parts.length > 0 ? parts.join(', ') : `Variant ${variant.id}`;
    };

    const handleVariantToggle = (variantId) => {
        if (selectedVariants.includes(variantId)) {
            onVariantSelect(selectedVariants.filter(id => id !== variantId));
        } else {
            onVariantSelect([...selectedVariants, variantId]);
        }
    };

    const handleSelectAll = () => {
        if (selectedVariants.length === variants.length) {
            onVariantSelect([]);
        } else {
            onVariantSelect(variants.map(v => v.id));
        }
    };

    if (!variants || variants.length === 0) {
        return (
            <div className="variant-selector-empty">
                <p>No variants available for this product.</p>
            </div>
        );
    }

    return (
        <div className="variant-selector">
            <div className="variant-selector-header">
                <h4>Select Variants ({selectedVariants.length}/{variants.length})</h4>
                <button
                    type="button"
                    className="select-all-btn"
                    onClick={handleSelectAll}
                >
                    {selectedVariants.length === variants.length ? 'Deselect All' : 'Select All'}
                </button>
            </div>

            <div className="variant-list">
                {variants.map((variant) => (
                    <div key={variant.id} className="variant-item">
                        <label className="variant-checkbox">
                            <input
                                type="checkbox"
                                checked={selectedVariants.includes(variant.id)}
                                onChange={() => handleVariantToggle(variant.id)}
                            />
                            <div className="variant-info">
                                <span className="variant-name">
                                    {getVariantDisplayName(variant)}
                                </span>
                                <div className="variant-details">
                                    <span className="variant-price">
                                        {storeCurrency} {Number(variant.price || 0).toFixed(2)}
                                    </span>
                                    <span className="variant-stock">
                                        Stock: {variant.quantity || 0}
                                    </span>
                                    {variant.sku && (
                                        <span className="variant-sku">
                                            SKU: {variant.sku}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </label>
                    </div>
                ))}
            </div>

            {selectedVariants.length === 0 && (
                <div className="variant-warning">
                    <p>⚠️ Please select at least one variant to apply the discount.</p>
                </div>
            )}
        </div>
    );
};

export default VariantSelector;